import React, { Component } from 'react';
import DOMPurify from 'dompurify'; // npm install dompurify (if not yet installed)
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const DRAFT_STATUS_CHIPS = {
  draft: { label: 'Brouillon', color: 'default' },
  edited: { label: 'Modifié', color: 'info' },
  published: { label: 'Publié', color: 'success' },
  copied: { label: 'Copié', color: 'warning' }
};

class DraftCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editOpen: false,
      editTitle: '',
      editContent: '',
      copied: false
    };
    this._isMounted = false;
    this._copyTimer = null;
    this.openEdit = this.openEdit.bind(this);
    this.closeEdit = this.closeEdit.bind(this);
    this.saveEdit = this.saveEdit.bind(this);
    this.handleCopy = this.handleCopy.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this._copyTimer) {
      clearTimeout(this._copyTimer);
      this._copyTimer = null;
    }
  }

  openEdit() {
    const { draft } = this.props;
    this.setState({
      editOpen: true,
      editTitle: draft.title || '',
      editContent: draft.content || ''
    });
  }

  closeEdit() {
    this.setState({ editOpen: false });
  }

  saveEdit() {
    const { editTitle, editContent } = this.state;
    this.props.onEdit(editContent, editTitle);
    this.setState({ editOpen: false });
  }

  handleCopy() {
    const { draft } = this.props;
    const text = draft.title ? `${draft.title}\n\n${draft.content}` : draft.content;
    navigator.clipboard.writeText(text || '').then(() => {
      if (!this._isMounted) return;
      this.setState({ copied: true });
      this._copyTimer = setTimeout(() => {
        if (this._isMounted) this.setState({ copied: false });
      }, 2000);
      if (this.props.onCopy) this.props.onCopy();
    }).catch(() => {
      // Clipboard API may fail in non-HTTPS or restricted contexts
    });
  }

  renderHtmlPreview(html) {
    return (
      <Box
        sx={{ fontSize: 14, lineHeight: 1.6, '& img': { maxWidth: '100%' } }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html || '') }}
      />
    );
  }

  render() {
    const { draft, channelLabel, channelIcon, imageUrl, showCharCount, showHtmlPreview, publishLabel, onPublish, publishEnabled } = this.props;
    const { editOpen, editTitle, editContent, copied } = this.state;
    const chip = DRAFT_STATUS_CHIPS[draft.status] || DRAFT_STATUS_CHIPS.draft;
    const charLimit = 280;
    // URL de publication finale (stockée en base après publication). On ne propose le lien
    // que si c'est une vraie URL http(s) : WordPress (lien d'édition) et Zendesk (article).
    // News in-app a un external_url = _id (pas une page) → pas de lien.
    const publishedUrl = /^https?:\/\//.test(draft.external_url || '') ? draft.external_url : null;
    const publishedUrlLabel = draft.channel === 'wordpress'
      ? 'Ouvrir le brouillon WordPress'
      : draft.channel === 'zendesk'
        ? "Voir l'article Zendesk"
        : draft.channel === 'facebook'
          ? 'Voir le post Facebook'
          : draft.channel === 'linkedin'
            ? 'Voir le post LinkedIn'
            : 'Voir la publication';

    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {channelIcon}
              <Typography variant="subtitle1" fontWeight={600}>{channelLabel}</Typography>
            </Box>
            <Chip size="small" label={chip.label} color={chip.color} />
          </Box>

          {imageUrl && (
            <Box sx={{ mb: 1 }}>
              <img src={imageUrl} alt={channelLabel} style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 4 }} />
            </Box>
          )}

          {draft.title && (
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{draft.title}</Typography>
          )}

          {showHtmlPreview
            ? this.renderHtmlPreview(draft.content)
            : (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {draft.content}
              </Typography>
            )}

          {showCharCount && (
            <Typography
              variant="caption"
              color={(draft.content || '').length > charLimit ? 'error' : 'text.secondary'}
              sx={{ mt: 1, display: 'block' }}
            >
              {(draft.content || '').length}/{charLimit} caractères
            </Typography>
          )}
        </CardContent>

        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 1.5 }}>
          <Tooltip title="Éditer">
            <IconButton size="small" onClick={this.openEdit}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={copied ? 'Copié !' : 'Copier'}>
            <IconButton size="small" onClick={this.handleCopy}>
              {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {publishedUrl && (
            <Tooltip title={publishedUrlLabel}>
              <Button
                size="small"
                startIcon={<OpenInNewIcon fontSize="small" />}
                href={publishedUrl}
                target="_blank"
                rel="noopener"
              >
                Voir
              </Button>
            </Tooltip>
          )}
          {imageUrl && (!publishLabel || ['twitter', 'facebook', 'linkedin'].includes(draft.channel)) && (
            <Tooltip title="Télécharger l'image pour l'attacher au post">
              <Button size="small" startIcon={<DownloadIcon fontSize="small" />} href={imageUrl} target="_blank" rel="noopener">
                Image
              </Button>
            </Tooltip>
          )}
          {publishLabel && (
            <Tooltip title={publishEnabled ? publishLabel : 'Bientôt disponible'}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!publishEnabled}
                  onClick={onPublish}
                >
                  {publishLabel}
                </Button>
              </span>
            </Tooltip>
          )}
        </CardActions>

        <Dialog open={editOpen} onClose={this.closeEdit} maxWidth="sm" fullWidth>
          <DialogTitle>Éditer — {channelLabel}</DialogTitle>
          <DialogContent>
            {['wordpress', 'zendesk', 'news_inapp'].includes(draft.channel) && (
              <TextField
                label="Titre"
                fullWidth
                value={editTitle}
                onChange={(e) => this.setState({ editTitle: e.target.value })}
                sx={{ mt: 1, mb: 2 }}
              />
            )}
            <TextField
              label="Contenu"
              fullWidth
              multiline
              rows={8}
              value={editContent}
              onChange={(e) => this.setState({ editContent: e.target.value })}
              sx={{ mt: ['wordpress', 'zendesk', 'news_inapp'].includes(draft.channel) ? 0 : 1 }}
            />
            {showCharCount && (
              <Typography
                variant="caption"
                color={editContent.length > charLimit ? 'error' : 'text.secondary'}
                sx={{ mt: 0.5, display: 'block' }}
              >
                {editContent.length}/{charLimit} caractères
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeEdit}>Annuler</Button>
            <Button variant="contained" onClick={this.saveEdit}>Enregistrer</Button>
          </DialogActions>
        </Dialog>
      </Card>
    );
  }
}

export default DraftCard;
